import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { createMaterial, updateMaterial, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PRESET_UNITS = ["stk", "m", "m²", "kg", "pakke", "rulle", "liter"];
const EMPTY = { name: "", unit: "stk", stock: 0, min_qty: 0, usage_month: 0, buy_price: 0, sell_price: 0, supplier: "", link: "", shelf: "" };

export default function MaterialFormDialog({ open, material, onClose, onSaved }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [customUnit, setCustomUnit] = useState(false);
    const isEdit = !!material?.id;

    useEffect(() => {
        if (open) {
            const init = material ? { ...EMPTY, ...material } : EMPTY;
            if (!init.unit) init.unit = "stk";
            setForm(init);
            setCustomUnit(init.unit && !PRESET_UNITS.includes(init.unit));
        }
    }, [open, material]);

    const update = (k, isNum) => (e) => setForm((f) => ({ ...f, [k]: isNum ? parseFloat(e.target.value || 0) : e.target.value }));

    const onUnitSelect = (val) => {
        if (val === "__custom__") {
            setCustomUnit(true);
            setForm((f) => ({ ...f, unit: "" }));
        } else {
            setCustomUnit(false);
            setForm((f) => ({ ...f, unit: val }));
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        const unit = (form.unit || "").trim() || "stk";
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                unit,
                stock: Number(form.stock) || 0,
                min_qty: Number(form.min_qty) || 0,
                usage_month: Number(form.usage_month) || 0,
                buy_price: Number(form.buy_price) || 0,
                sell_price: Number(form.sell_price) || 0,
                supplier: form.supplier || "",
                link: form.link || "",
                shelf: form.shelf || "",
            };
            const saved = isEdit ? await updateMaterial(material.id, payload) : await createMaterial(payload);
            toast.success(isEdit ? "Materiale opdateret" : "Materiale oprettet");
            onSaved(saved);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{isEdit ? "Redigér materiale" : "Opret materiale"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                    <div className="sm:col-span-2">
                        <Label>Navn</Label>
                        <Input required data-testid="mat-form-name" value={form.name} onChange={update("name")} className="h-10 mt-1" />
                    </div>
                    <div>
                        <Label>Enhed</Label>
                        <select
                            data-testid="mat-form-unit-select"
                            value={customUnit ? "__custom__" : (form.unit || "stk")}
                            onChange={(e) => onUnitSelect(e.target.value)}
                            className="w-full h-10 mt-1 rounded-md border border-slate-200 px-2 text-sm"
                        >
                            {PRESET_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                            <option value="__custom__">Andet…</option>
                        </select>
                        {customUnit && (
                            <Input
                                data-testid="mat-form-unit-custom"
                                value={form.unit}
                                onChange={update("unit")}
                                placeholder="Fx sæk, tromle"
                                maxLength={20}
                                className="h-9 mt-1.5 text-sm"
                            />
                        )}
                    </div>
                    <div>
                        <Label>Lager</Label>
                        <div className="flex items-center gap-1.5 mt-1">
                            <Input type="number" step="0.01" data-testid="mat-form-stock" value={form.stock} onChange={update("stock", true)} className="h-10" />
                            <span className="text-sm text-slate-500 w-14 shrink-0">{form.unit || "stk"}</span>
                        </div>
                    </div>
                    <div>
                        <Label>Minimum antal</Label>
                        <div className="flex items-center gap-1.5 mt-1">
                            <Input type="number" step="0.01" data-testid="mat-form-min" value={form.min_qty} onChange={update("min_qty", true)} className="h-10" />
                            <span className="text-sm text-slate-500 w-14 shrink-0">{form.unit || "stk"}</span>
                        </div>
                    </div>
                    <div>
                        <Label>Hyldeplacering</Label>
                        <Input data-testid="mat-form-shelf" value={form.shelf} onChange={update("shelf")} placeholder="fx A-12" className="h-10 mt-1" />
                    </div>
                    <div>
                        <Label>Købspris (kr.)</Label>
                        <Input type="number" step="0.01" data-testid="mat-form-buy" value={form.buy_price} onChange={update("buy_price", true)} className="h-10 mt-1" />
                    </div>
                    <div>
                        <Label>Salgspris (kr.)</Label>
                        <Input type="number" step="0.01" data-testid="mat-form-sell" value={form.sell_price} onChange={update("sell_price", true)} className="h-10 mt-1" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Leverandør</Label>
                        <Input data-testid="mat-form-supplier" value={form.supplier} onChange={update("supplier")} className="h-10 mt-1" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Link til hjemmeside / produkt</Label>
                        <Input data-testid="mat-form-link" value={form.link} onChange={update("link")} placeholder="https://" className="h-10 mt-1" />
                    </div>
                    <div className="sm:col-span-2 flex justify-end gap-2 mt-2">
                        <Button type="button" variant="outline" onClick={onClose}>Annullér</Button>
                        <Button type="submit" disabled={saving} data-testid="mat-form-submit" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            {isEdit ? "Gem ændringer" : "Opret materiale"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
